package com.fantasytenis.web.screens.home

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.bracket.entity.BracketEntryEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketEntryStatus
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.contests.entity.EntryStatus
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.survivor.entity.SurvivorEntryEntity
import com.fantasytenis.shared.domain.survivor.entity.SurvivorEntryStatus
import com.fantasytenis.shared.domain.tennis.entity.MatchEntity
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ConfirmationDialog
import com.fantasytenis.web.components.LiveMatchTicker
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.components.ThemeToggleIcon
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import com.fantasytenis.web.theme.ThemeState
import com.fantasytenis.web.utils.ContestUtils
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun HomeScreen() {
    val liveScores by SharedData.liveMatches.collectAsState()
    val scope = rememberCoroutineScope()
    val colors = Theme.colors
    val realtimeState = useRealtime()

    // Data states
    var perfectPlayerEntries by remember { mutableStateOf<List<EntryEntity>>(emptyList()) }
    var bracketEntries by remember { mutableStateOf<List<BracketEntryEntity>>(emptyList()) }
    var survivorEntries by remember { mutableStateOf<List<SurvivorEntryEntity>>(emptyList()) }
    var liveMatches by remember { mutableStateOf<List<MatchEntity>>(emptyList()) }
    var closingContests by remember { mutableStateOf<List<ContestEntity>>(emptyList()) }

    // Loading states
    var isLoadingEntries by remember { mutableStateOf(true) }
    var isLoadingMatches by remember { mutableStateOf(true) }
    var isLoadingContests by remember { mutableStateOf(true) }

    // Load data on mount
    LaunchedEffect(Unit) {
        // Fetch Perfect Player entries
        DependencyContainer.getUserContestEntriesUseCase().fold(
            onSuccess = { entries ->
                perfectPlayerEntries = entries.filter { it.status.isVisibleOnHome }
                SharedData.myActiveEntries.value = perfectPlayerEntries
            },
            onFailure = { /* Non-critical */ }
        )

        // Fetch Bracket entries
        DependencyContainer.getUserBracketEntriesUseCase().fold(
            onSuccess = { entries ->
                bracketEntries = entries.filter {
                    it.status in listOf(BracketEntryStatus.PENDING, BracketEntryStatus.SUBMITTED, BracketEntryStatus.LIVE)
                }
            },
            onFailure = { /* Non-critical */ }
        )

        // Fetch Survivor entries
        DependencyContainer.getUserSurvivorEntriesUseCase().fold(
            onSuccess = { entries ->
                survivorEntries = entries.filter { it.status.isAlive }
            },
            onFailure = { /* Non-critical */ }
        )

        isLoadingEntries = false

        // Fetch live matches
        DependencyContainer.getLiveMatchesUseCase().fold(
            onSuccess = { matches ->
                liveMatches = matches
            },
            onFailure = { /* Non-critical */ }
        )
        isLoadingMatches = false

        // Fetch upcoming contests for "closing soon"
        val showPaidGamesValue = SharedData.showPaidGames.value
        DependencyContainer.listContestsUseCase(status = 1, limit = 20).fold(
            onSuccess = { contests ->
                val now = kotlin.js.Date().getTime().toLong()
                val twoHoursMs = 2 * 60 * 60 * 1000L
                closingContests = contests
                    .let { list -> if (showPaidGamesValue) list else list.filter { it.entryFee == 0.0 } }
                    .filter { contest ->
                        val lockTime = contest.rosterLockTime
                        lockTime > now && (lockTime - now) < twoHoursMs
                    }.take(3)
            },
            onFailure = { /* Non-critical */ }
        )
        isLoadingContests = false
    }

    // Subscribe to live matches when connected
    LaunchedEffect(realtimeState.isConnected, liveMatches) {
        if (realtimeState.isConnected && liveMatches.isNotEmpty()) {
            liveMatches.forEach { match ->
                DependencyContainer.realtimeConnection.subscribeToMatch(match.id)
            }
        }
    }

    // Cleanup subscriptions when leaving
    DisposableEffect(Unit) {
        onDispose {
            liveMatches.forEach { match ->
                scope.launch {
                    DependencyContainer.realtimeConnection.unsubscribeFromMatch(match.id)
                }
            }
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Responsive Header - handles mobile menu internally
        ResponsiveHeader(title = "Baseline")

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            // Your Entries Section
            YourEntriesSection(
                perfectPlayerEntries = perfectPlayerEntries,
                bracketEntries = bracketEntries,
                survivorEntries = survivorEntries,
                isLoading = isLoadingEntries,
                colors = colors
            )

            // Live Matches Widget
            LiveMatchesWidget(
                matches = liveMatches,
                liveScores = liveScores,
                isLoading = isLoadingMatches,
                isConnected = realtimeState.isConnected,
                colors = colors
            )

            // Closing Soon Section
            if (!isLoadingContests && closingContests.isNotEmpty()) {
                ClosingSoonSection(
                    contests = closingContests,
                    colors = colors
                )
            }

            // Browse Section (demoted quick actions)
            BrowseSection(colors = colors)
            }
        }
    }
}

// ============================================================================
// YOUR ENTRIES SECTION
// ============================================================================

@Composable
private fun YourEntriesSection(
    perfectPlayerEntries: List<EntryEntity>,
    bracketEntries: List<BracketEntryEntity>,
    survivorEntries: List<SurvivorEntryEntity>,
    isLoading: Boolean,
    colors: ThemeColors
) {
    val totalEntries = perfectPlayerEntries.size + bracketEntries.size + survivorEntries.size
    val hasEntries = totalEntries > 0

    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        // Section header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            H3({
                style {
                    h3Style()
                    color(colors.textPrimary)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Text("Your Entries")
                if (hasEntries) {
                    Span({
                        style {
                            backgroundColor(colors.primary)
                            color(Colors.White)
                            fontSize(12.px)
                            fontWeight(600)
                            padding(2.px, 8.px)
                            borderRadius(10.px)
                        }
                    }) {
                        Text(totalEntries.toString())
                    }
                }
            }

            if (hasEntries) {
                Span({
                    style {
                        fontSize(14.px)
                        color(colors.primary)
                        cursor("pointer")
                        fontWeight(500)
                    }
                    onClick { NavigationState.navigate(Route.MyEntries) }
                }) {
                    Text("View All →")
                }
            }
        }

        when {
            isLoading -> SkeletonEntriesRow()
            !hasEntries -> EmptyEntriesState(colors)
            else -> {
                // Responsive grid: horizontal on desktop, vertical stack on mobile
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 200px), 1fr))")
                        gap(16.px)
                    }
                    classes("entries-grid")
                }) {
                    // Perfect Player entries
                    perfectPlayerEntries.forEach { entry ->
                        PerfectPlayerEntryCard(entry, colors)
                    }

                    // Bracket entries
                    bracketEntries.forEach { entry ->
                        BracketEntryCard(entry, colors)
                    }

                    // Survivor entries
                    survivorEntries.forEach { entry ->
                        SurvivorEntryCard(entry, colors)
                    }
                }
            }
        }
    }
}

@Composable
private fun PerfectPlayerEntryCard(entry: EntryEntity, colors: ThemeColors) {
    // Navigate to Game Day for live entries, otherwise to leaderboard
    val isLive = entry.status == EntryStatus.LIVE
    val onClick = {
        if (isLive) {
            NavigationState.navigate(Route.GameDay(entry.contestId, entry.id))
        } else {
            NavigationState.navigate(Route.ContestLeaderboard(entry.contestId))
        }
    }

    EntryCardBase(
        gameType = if (isLive) "Perfect Player \uD83D\uDD34" else "Perfect Player",
        gameTypeColor = if (isLive) Colors.Live else colors.primary,
        onClick = onClick,
        colors = colors
    ) {
        // Points display
        Div({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(colors.textPrimary)
                marginBottom(4.px)
            }
        }) {
            Text("${entry.totalPoints.toInt()} pts")
        }

        // Rank
        entry.rank?.let { rank ->
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("#$rank")
            }
        }

        // Live indicator
        if (isLive) {
            Div({
                style {
                    marginTop(6.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                }
            }) {
                Span({
                    classes("game-day-live-indicator")
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(Colors.Live)
                    }
                })
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.Live)
                        fontWeight(600)
                    }
                }) {
                    Text("Watch Live")
                }
            }
        }

        // Payout if any
        if (entry.payoutAmount > 0) {
            Div({
                style {
                    marginTop(8.px)
                    fontSize(12.px)
                    color(Colors.Success)
                    fontWeight(500)
                }
            }) {
                Text("$${entry.payoutAmount.asDynamic().toFixed(2)}")
            }
        }
    }
}

@Composable
private fun BracketEntryCard(entry: BracketEntryEntity, colors: ThemeColors) {
    val statusText = when (entry.status) {
        BracketEntryStatus.PENDING -> "Picks pending"
        BracketEntryStatus.SUBMITTED -> "Submitted"
        BracketEntryStatus.LIVE -> "Live"
        else -> entry.status.name
    }

    val statusColor = when (entry.status) {
        BracketEntryStatus.PENDING -> Colors.Warning
        BracketEntryStatus.LIVE -> Colors.Error
        else -> colors.textSecondary
    }

    EntryCardBase(
        gameType = "Bracket",
        gameTypeColor = Colors.Accent,
        onClick = { NavigationState.navigate(Route.BracketChallengeDetail(entry.challengeId)) },
        colors = colors
    ) {
        // Status with indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(4.px)
            }
        }) {
            if (entry.status == BracketEntryStatus.LIVE) {
                Span({
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(Colors.Error)
                        property("animation", "pulse 1.5s infinite")
                    }
                })
            }
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(600)
                    color(statusColor)
                }
            }) {
                Text(statusText)
            }
        }

        // Pending picks count
        if (entry.pendingPicks > 0) {
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${entry.pendingPicks} picks remaining")
            }
        } else {
            // Show points if submitted
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${entry.correctPicks}/${entry.correctPicks + entry.incorrectPicks} correct")
            }
        }

        // Rank if available
        entry.rank?.let { rank ->
            Div({
                style {
                    marginTop(4.px)
                    fontSize(12.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("#$rank")
            }
        }
    }
}

@Composable
private fun SurvivorEntryCard(entry: SurvivorEntryEntity, colors: ThemeColors) {
    val statusText = when (entry.status) {
        SurvivorEntryStatus.ALIVE -> "Alive"
        SurvivorEntryStatus.ELIMINATED -> "Eliminated"
        SurvivorEntryStatus.REBUY_ELIGIBLE -> "Can Rebuy"
        SurvivorEntryStatus.WINNER -> "Winner!"
        else -> entry.status.name
    }

    val statusColor = when (entry.status) {
        SurvivorEntryStatus.ALIVE -> Colors.Success
        SurvivorEntryStatus.WINNER -> Colors.Secondary
        SurvivorEntryStatus.ELIMINATED -> Colors.Error
        SurvivorEntryStatus.REBUY_ELIGIBLE -> Colors.Warning
        else -> colors.textSecondary
    }

    EntryCardBase(
        gameType = "Survivor",
        gameTypeColor = Colors.Error,
        onClick = { NavigationState.navigate(Route.SurvivorContestDetail(entry.contestId)) },
        colors = colors
    ) {
        // Status
        Div({
            style {
                fontSize(16.px)
                fontWeight(700)
                color(statusColor)
                marginBottom(4.px)
            }
        }) {
            Text(statusText)
        }

        // Rounds survived
        Div({
            style {
                fontSize(13.px)
                color(colors.textSecondary)
            }
        }) {
            Text("${entry.survivedRounds} rounds survived")
        }

        // Rank if available
        entry.rank?.let { rank ->
            Div({
                style {
                    marginTop(4.px)
                    fontSize(12.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("#$rank")
            }
        }
    }
}

@Composable
private fun EntryCardBase(
    gameType: String,
    gameTypeColor: CSSColorValue,
    onClick: () -> Unit,
    colors: ThemeColors,
    content: @Composable () -> Unit
) {
    val isDark by ThemeState.isDarkMode

    Div({
        classes("baseline-card", "entry-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px)
            cursor("pointer")
            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick { onClick() }
    }) {
        // Game type badge
        Span({
            style {
                backgroundColor(gameTypeColor)
                color(Colors.White)
                fontSize(10.px)
                fontWeight(700)
                padding(3.px, 8.px)
                borderRadius(4.px)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(gameType)
        }

        Div({
            style {
                marginTop(12.px)
            }
        }) {
            content()
        }
    }
}

@Composable
private fun EmptyEntriesState(colors: ThemeColors) {
    val isDark by ThemeState.isDarkMode

    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(32.px)
            textAlign("center")
            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
        }
    }) {
        Div({
            style {
                fontSize(32.px)
                marginBottom(12.px)
            }
        }) {
            Text("🎾")
        }

        H3({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(8.px)
            }
        }) {
            Text("No active entries")
        }

        P({
            style {
                fontSize(14.px)
                color(colors.textSecondary)
                marginBottom(20.px)
            }
        }) {
            Text("Enter a contest to start competing for prizes!")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                justifyContent(JustifyContent.Center)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button(
                text = "Perfect Player",
                onClick = { NavigationState.navigate(Route.Contests) },
                variant = "primary"
            )
            Button(
                text = "Brackets",
                onClick = { NavigationState.navigate(Route.BracketChallenges) },
                variant = "secondary"
            )
            Button(
                text = "Survivor",
                onClick = { NavigationState.navigate(Route.SurvivorContests) },
                variant = "secondary"
            )
        }
    }
}

@Composable
private fun SkeletonEntriesRow() {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(16.px)
        }
    }) {
        repeat(3) {
            Div({
                style {
                    backgroundColor(Color("#f0f0f0"))
                    borderRadius(12.px)
                    padding(16.px)
                    minWidth(180.px)
                }
            }) {
                Skeleton(width = 80.px, height = 16.px, borderRadius = 4.px)
                Div({ style { height(12.px) } })
                Skeleton(width = 100.px, height = 24.px)
                Div({ style { height(8.px) } })
                Skeleton(width = 60.px, height = 14.px)
            }
        }
    }
}

// ============================================================================
// LIVE MATCHES WIDGET
// ============================================================================

@Composable
private fun LiveMatchesWidget(
    matches: List<MatchEntity>,
    liveScores: Map<String, LiveScoreEntity>,
    isLoading: Boolean,
    isConnected: Boolean,
    colors: ThemeColors
) {
    val isDark by ThemeState.isDarkMode

    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(12.px)
                padding(16.px, 20.px)
                property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
            }
        }) {
            when {
                isLoading -> {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        Skeleton(width = 80.px, height = 20.px)
                        Skeleton(width = 200.px, height = 16.px)
                    }
                }
                matches.isEmpty() -> {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                        }
                    }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(8.px)
                            }
                        }) {
                            Span({
                                style {
                                    fontSize(14.px)
                                }
                            }) {
                                Text("🎾")
                            }
                            Span({
                                style {
                                    fontSize(14.px)
                                    color(colors.textSecondary)
                                }
                            }) {
                                Text("No live matches right now")
                            }
                        }

                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.primary)
                                cursor("pointer")
                                fontWeight(500)
                            }
                            onClick { NavigationState.navigate(Route.Matches) }
                        }) {
                            Text("View Schedule →")
                        }
                    }
                }
                else -> {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                            flexWrap(FlexWrap.Wrap)
                            gap(16.px)
                        }
                    }) {
                        // Live badge and match info
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(16.px)
                                flex(1)
                                minWidth(0.px)
                            }
                        }) {
                            // Live badge
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(6.px)
                                    backgroundColor(rgba(244, 67, 54, 0.1))
                                    padding(6.px, 12.px)
                                    borderRadius(16.px)
                                    flexShrink(0)
                                }
                            }) {
                                Span({
                                    style {
                                        width(8.px)
                                        height(8.px)
                                        borderRadius(50.percent)
                                        backgroundColor(Colors.Error)
                                        property("animation", "pulse 1.5s infinite")
                                    }
                                })
                                Span({
                                    style {
                                        fontSize(12.px)
                                        fontWeight(700)
                                        color(Colors.Error)
                                    }
                                }) {
                                    Text("${matches.size} LIVE")
                                }
                            }

                            // Match ticker with auto-rotation
                            LiveMatchTicker(
                                matches = matches,
                                liveScores = liveScores,
                                colors = colors
                            )
                        }

                        // View all link
                        Span({
                            style {
                                fontSize(13.px)
                                color(colors.primary)
                                cursor("pointer")
                                fontWeight(500)
                                flexShrink(0)
                            }
                            onClick { NavigationState.navigate(Route.Matches) }
                        }) {
                            Text("View All →")
                        }
                    }
                }
            }
        }
    }
}

// ============================================================================
// CLOSING SOON SECTION
// ============================================================================

@Composable
private fun ClosingSoonSection(
    contests: List<ContestEntity>,
    colors: ThemeColors
) {
    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        // Section header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            Span({
                style {
                    fontSize(16.px)
                }
            }) {
                Text("⏰")
            }
            H3({
                style {
                    h3Style()
                    color(colors.textPrimary)
                }
            }) {
                Text("Closing Soon")
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            contests.forEach { contest ->
                ClosingContestCard(contest, colors)
            }
        }
    }
}

@Composable
private fun ClosingContestCard(contest: ContestEntity, colors: ThemeColors) {
    val isDark by ThemeState.isDarkMode
    val countdown = remember(contest.rosterLockTime) {
        calculateCountdown(contest.rosterLockTime)
    }

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(16.px, 20.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(16.px)
            cursor("pointer")
            property("box-shadow", if (isDark) "0 2px 4px rgba(0, 0, 0, 0.3)" else "0 2px 4px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
            flexWrap(FlexWrap.Wrap)
        }
        onClick { NavigationState.navigate(Route.ContestDetail(contest.id)) }
    }) {
        // Contest info
        Div({
            style {
                flex(1)
                minWidth(200.px)
            }
        }) {
            Div({
                style {
                    fontSize(15.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                    marginBottom(4.px)
                }
            }) {
                Text(ContestUtils.getDisplayName(contest.name))
            }
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(contest.tournamentName)
            }
        }

        // Entry/Prize info
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(20.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Entry fee
            Div({
                style {
                    textAlign("center")
                }
            }) {
                Div({
                    style {
                        fontSize(11.px)
                        color(colors.textSecondary)
                        marginBottom(2.px)
                    }
                }) {
                    Text("Entry")
                }
                Div({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    val showPaidGames = SharedData.showPaidGames.value
                    Text(if (contest.entryFee == 0.0 && showPaidGames) "FREE" else if (contest.entryFee == 0.0) "$0" else "$${contest.entryFee.toInt()}")
                }
            }

            // Prize pool
            Div({
                style {
                    textAlign("center")
                }
            }) {
                Div({
                    style {
                        fontSize(11.px)
                        color(colors.textSecondary)
                        marginBottom(2.px)
                    }
                }) {
                    Text("Prize")
                }
                Div({
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(Colors.Success)
                    }
                }) {
                    Text("$${contest.prizePool.toInt()}")
                }
            }

            // Countdown
            Div({
                style {
                    backgroundColor(rgba(255, 152, 0, 0.1))
                    padding(6.px, 12.px)
                    borderRadius(16.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(Colors.Warning)
                    }
                }) {
                    Text(countdown)
                }
            }
        }

        // Enter button
        Button(
            text = "Enter",
            onClick = { NavigationState.navigate(Route.ContestDetail(contest.id)) },
            variant = "primary"
        )
    }
}

private fun calculateCountdown(lockTimeMs: Long): String {
    val now = kotlin.js.Date().getTime().toLong()
    val diffMs = lockTimeMs - now

    if (diffMs <= 0) return "Locking..."

    val diffMinutes = (diffMs / 60000).toInt()
    val hours = diffMinutes / 60
    val minutes = diffMinutes % 60

    return when {
        diffMinutes < 60 -> "Locks in ${minutes}m"
        else -> "Locks in ${hours}h ${minutes}m"
    }
}

// ============================================================================
// BROWSE SECTION
// ============================================================================

@Composable
private fun BrowseSection(colors: ThemeColors) {
    Section({
        style {
            marginTop(16.px)
        }
    }) {
        H3({
            style {
                h3Style()
                color(colors.textSecondary)
                marginBottom(16.px)
                fontSize(14.px)
                fontWeight(500)
                property("text-transform", "uppercase")
                letterSpacing(1.px)
            }
        }) {
            Text("Browse")
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 200px), 1fr))")
                gap(16.px)
            }
        }) {
            BrowseCard(
                title = "Perfect Player",
                description = "Daily lineup contests",
                onClick = { NavigationState.navigate(Route.Contests) },
                colors = colors
            )

            BrowseCard(
                title = "Bracket Challenges",
                description = "Tournament predictions",
                onClick = { NavigationState.navigate(Route.BracketChallenges) },
                colors = colors
            )

            BrowseCard(
                title = "Survivor",
                description = "Pick to survive",
                onClick = { NavigationState.navigate(Route.SurvivorContests) },
                colors = colors
            )

            BrowseCard(
                title = "Match Center",
                description = "Scores & schedule",
                onClick = { NavigationState.navigate(Route.Matches) },
                colors = colors
            )

            BrowseCard(
                title = "Skill Ratings",
                description = "Player rankings by skill",
                onClick = { NavigationState.navigate(Route.Skills) },
                colors = colors
            )
        }
    }
}

@Composable
private fun BrowseCard(
    title: String,
    description: String,
    onClick: () -> Unit,
    colors: ThemeColors
) {
    val isDark by ThemeState.isDarkMode

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(8.px)
            padding(16.px)
            cursor("pointer")
            property("box-shadow", if (isDark) "0 1px 2px rgba(0, 0, 0, 0.2)" else "0 1px 2px rgba(0, 0, 0, 0.05)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(4.px)
            }
        }) {
            Text(title)
        }
        Div({
            style {
                fontSize(12.px)
                color(colors.textSecondary)
            }
        }) {
            Text(description)
        }
    }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

private fun rgba(r: Int, g: Int, b: Int, a: Double): CSSColorValue {
    return Color("rgba($r, $g, $b, $a)")
}
